import { Modal } from 'react-bootstrap';
import Button from '../../../sf-components/Button/Button';
import ButtonStyles from '../../../sf-components/Button/ButtonStyles';
import { Label } from '@silverfort/sf-components';
import { useCallback, useEffect, useState } from 'react';
import Dropdown from '../../../sf-components/Dropdown/Dropdown';
import TextInput from '../../../sf-components/TextInput/TextInput';
import { httpGet, httpPost } from '../../../utils';
import {
    BACKUP_INTERVAL_OPTIONS,
    FILE_SYSTEM_OPTIONS,
    NEW_DAYS_OPTIONS
} from '@silverfort-nodelibs/common/utils/consts';
import { buildMonthDaysMap } from '@silverfort-nodelibs/common/utils/common';
import moment from 'moment-timezone';
import SMBFields from './SMBFields';
import { AZURE_AUTH_TYPE_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { TextInput as SFTextInput } from '@silverfort/sf-components';
import { isSasTokenExpired } from '@silverfort-nodelibs/common/utils/sasTokenUtils';

const monthDays = buildMonthDaysMap();

const parseSchedulerInterval = (scheduler, toUTC) => {
    const parseScheduler = { ...scheduler };
    const m = moment();
    if (!toUTC) {
        m.utc();
    }
    switch (scheduler.interval) {
        case BACKUP_INTERVAL_OPTIONS.never.value:
            return parseScheduler;
        case BACKUP_INTERVAL_OPTIONS.monthly.value: {
            const [hour, minutes] = scheduler.hour.split(':');
            // we set the month to 0 to represent january, as we offer selection of 31 days
            m.set({ month: 0, date: scheduler.date, hour: hour, minute: minutes });
            toUTC ? m.utc() : m.local();

            const hourStr = `${m.hour()}`.length === 1 ? `0${m.hour()}` : `${m.hour()}`;
            const minutesStr = `${m.minute()}`.length === 1 ? `0${m.minute()}` : `${m.minute()}`;
            parseScheduler.hour = `${hourStr}:${minutesStr}`;
            parseScheduler.date = m.date();
            break;
        }
        case BACKUP_INTERVAL_OPTIONS.weekly.value: {
            const [hour, minutes] = scheduler.hour.split(':');
            m.set({ day: scheduler.day, hour: hour, minute: minutes });
            toUTC ? m.utc() : m.local();

            const hourStr = `${m.hour()}`.length === 1 ? `0${m.hour()}` : `${m.hour()}`;
            const minutesStr = `${m.minute()}`.length === 1 ? `0${m.minute()}` : `${m.minute()}`;
            parseScheduler.hour = `${hourStr}:${minutesStr}`;
            parseScheduler.day = m.day();
            break;
        }
        case BACKUP_INTERVAL_OPTIONS.daily.value: {
            const [hour, minutes] = scheduler.hour.split(':');
            m.set({ hour: hour, minute: minutes });
            toUTC ? m.utc() : m.local();

            const hourStr = `${m.hour()}`.length === 1 ? `0${m.hour()}` : `${m.hour()}`;
            const minutesStr = `${m.minute()}`.length === 1 ? `0${m.minute()}` : `${m.minute()}`;
            parseScheduler.hour = `${hourStr}:${minutesStr}`;
            break;
        }
        default:
            return parseScheduler;
    }
    return parseScheduler;
};

const SchedulerBackup = ({
    onCreate,
    onClose,
    show,
    scheduleBackupRollingBack,
    backupPasswordInfo,
    getBackupPasswordError
}) => {
    const [scheduler, setScheduler] = useState({});
    const [schedulerRawData, setSchedulerRawData] = useState({});
    const [isScheduleValid, setIsScheduleValid] = useState(false);
    const [passwordError, setPasswordError] = useState();

    useEffect(() => {
        const getScheduler = async () => {
            if (!scheduleBackupRollingBack) {
                try {
                    const response = await httpGet('/Settings/getBackupScheduler');

                    setSchedulerRawData(response.data.scheduler);
                    const parsedScheduler = parseSchedulerInterval(response.data.scheduler, false);
                    setScheduler(parsedScheduler);
                } catch (err) {
                    console.error(err);
                }
            }
        };
        getScheduler();
    }, [scheduleBackupRollingBack]);

    const cancelChanges = async () => {
        const parsedScheduler = parseSchedulerInterval(schedulerRawData, false);
        setScheduler(parsedScheduler);
        onClose();
    };

    useEffect(() => {
        let isIntervalValid = false;
        switch (scheduler.interval) {
            case BACKUP_INTERVAL_OPTIONS.never.value:
                return setIsScheduleValid(true);
            case BACKUP_INTERVAL_OPTIONS.monthly.value:
                isIntervalValid = scheduler.date !== undefined && scheduler.hour;
                break;
            case BACKUP_INTERVAL_OPTIONS.weekly.value:
                isIntervalValid = scheduler.day !== undefined && scheduler.hour;
                break;
            case BACKUP_INTERVAL_OPTIONS.daily.value:
                isIntervalValid = !!scheduler.hour;
                break;
            default:
                isIntervalValid = false;
        }

        if (!scheduler.password) {
            return setIsScheduleValid(false);
        }
        let isFileSystemValid = false;
        switch (scheduler.fileSystem) {
            case FILE_SYSTEM_OPTIONS.aws.value:
                isFileSystemValid =
                    scheduler.awsRegion &&
                    scheduler.awsAccessKeyId &&
                    scheduler.awsSecretAccessKey &&
                    scheduler.awsBucketName;
                break;
            case FILE_SYSTEM_OPTIONS.azure.value:
                isFileSystemValid =
                    (scheduler.authType === AZURE_AUTH_TYPE_OPTIONS.sasToken.value
                        ? scheduler.azureSasToken && !isSasTokenExpired(scheduler.azureSasToken)
                        : scheduler.azureAccountKey) &&
                    scheduler.azureAccount &&
                    scheduler.azureContainer;
                break;
            case FILE_SYSTEM_OPTIONS.smb.value:
                isFileSystemValid =
                    scheduler.smbHost &&
                    scheduler.smbSharedPath &&
                    scheduler.smbShareDomain &&
                    scheduler.smbUserName &&
                    scheduler.smbUserPassword;
                break;
            default:
                isFileSystemValid = false;
        }

        setIsScheduleValid(isIntervalValid && isFileSystemValid);
    }, [scheduler]);

    const handleSave = () => {
        const error = getBackupPasswordError(scheduler.password);
        if (error) {
            setPasswordError(error);
            return;
        }
        onCreate(saveScheduler, getSchedulerDescription());
    };

    const saveScheduler = async () => {
        const parsedSchedulerUTC = parseSchedulerInterval(scheduler, true);
        const response = await httpPost('/Settings/setBackupScheduler', { scheduler: parsedSchedulerUTC });
        setSchedulerRawData(response.data.scheduler);
        const parsedSchedulerLocal = parseSchedulerInterval(response.data.scheduler, false);
        setScheduler(parsedSchedulerLocal);
    };

    const getSchedulerDescription = () => {
        if (scheduler.interval === BACKUP_INTERVAL_OPTIONS.never.value) {
            return 'System backup set to Never';
        }
        const baseDescription = 'System backup scheduled ';
        if (scheduler.interval === BACKUP_INTERVAL_OPTIONS.monthly.value) {
            return `${baseDescription}for ${monthDays[scheduler.date].label} at ${scheduler.hour}. Repeats monthly.`;
        }
        if (scheduler.interval === BACKUP_INTERVAL_OPTIONS.weekly.value) {
            return `${baseDescription}for ${NEW_DAYS_OPTIONS[scheduler.day].label} at ${
                scheduler.hour
            }. Repeats weekly.`;
        }
        if (scheduler.interval === BACKUP_INTERVAL_OPTIONS.daily.value) {
            return `${baseDescription}at ${scheduler.hour}, Repeats daily.`;
        }
    };

    const updateSchedulerField = useCallback(
        (fieldName, value) => {
            if (!fieldName) {
                console.error('field name not assigned');
                return;
            }
            setScheduler((scheduler) => {
                return { ...scheduler, [fieldName]: value };
            });
        },
        [setScheduler]
    );

    return (
        <Modal
            className="font-sf flex flex-col justify-center"
            id={'SchedulersBackup'}
            dialogClassName="w-[432px] h-fit [&_.modal-content]:rounded-lg [&_.modal-content]:shadow-modal-light"
            show={show}
            onHide={cancelChanges}
            keyboard={false}
            backdrop="static">
            <Modal.Header className="border-hidden pt-4 pr-6 pb-0 pl-0" closeButton>
                <div className="px-4 pb-4 mb-1 text-14 font-medium text-sf-gray-800">Schedule Backup</div>
            </Modal.Header>
            <Modal.Body className="text-13 px-4 pb-6 h-full border-t border-b  overflow-y-auto">
                <div className="grid gap-8">
                    <div className="grid gap-4">
                        <div className="text-xs font-medium text-sf-gray-500">Schedule</div>
                        <div className="flex items-center">
                            <div className="min-w-[128px]">
                                <Label text={'Frequency'} />
                            </div>
                            <div>
                                <Dropdown
                                    currentOption={scheduler.interval}
                                    onOptionChange={(interval) =>
                                        setScheduler((scheduler) => {
                                            return { ...scheduler, interval };
                                        })
                                    }
                                    options={BACKUP_INTERVAL_OPTIONS}
                                    labelClassName="w-[135px]"
                                    menuClassName="w-[135px]"
                                    required
                                />
                            </div>
                        </div>
                        {scheduler.interval === BACKUP_INTERVAL_OPTIONS.weekly.value && (
                            <div className="flex items-center">
                                <div className="min-w-[128px]">
                                    <Label text={'Every'} />
                                </div>
                                <div>
                                    <Dropdown
                                        currentOption={scheduler.day || NEW_DAYS_OPTIONS[0].value}
                                        onOptionChange={(day) =>
                                            setScheduler((scheduler) => {
                                                return { ...scheduler, day };
                                            })
                                        }
                                        options={NEW_DAYS_OPTIONS}
                                        labelClassName="w-[135px]"
                                        menuClassName="w-[135px]"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        {scheduler.interval === BACKUP_INTERVAL_OPTIONS.monthly.value && (
                            <div className="flex items-center">
                                <div className="min-w-[128px]">
                                    <Label text={'Every'} />
                                </div>
                                <div>
                                    <Dropdown
                                        currentOption={scheduler.date || monthDays[1].value}
                                        onOptionChange={(date) =>
                                            setScheduler((scheduler) => {
                                                return { ...scheduler, date };
                                            })
                                        }
                                        options={monthDays}
                                        labelClassName="w-[135px]"
                                        menuClassName="w-[135px] max-h-50 overflow-auto"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        {scheduler.interval !== BACKUP_INTERVAL_OPTIONS.never.value && (
                            <div className="flex items-center">
                                <div className="min-w-[128px]">
                                    <Label text={'On'} />
                                </div>
                                <div>
                                    <TextInput
                                        onChange={(hour) =>
                                            setScheduler((scheduler) => {
                                                return { ...scheduler, hour };
                                            })
                                        }
                                        value={scheduler.hour}
                                        type="time"
                                        className="w-[120px]"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        <div className="text-xs font-medium text-sf-gray-500">Path setup</div>
                        <div className="flex items-center">
                            <div className="min-w-[128px]">
                                <Label text={'Type'} />
                            </div>
                            <div>
                                <Dropdown
                                    currentOption={scheduler.fileSystem}
                                    onOptionChange={(fileSystem) =>
                                        setScheduler((scheduler) => {
                                            return { ...scheduler, fileSystem };
                                        })
                                    }
                                    options={FILE_SYSTEM_OPTIONS}
                                    labelClassName="w-[135px]"
                                    menuClassName="w-[135px]"
                                />
                            </div>
                        </div>
                        {scheduler.fileSystem === FILE_SYSTEM_OPTIONS.azure.value && (
                            <>
                                <div className="flex items-center">
                                    <div className="min-w-[128px]">
                                        <Label text={'Authentication Type'} />
                                    </div>
                                    <div>
                                        <Dropdown
                                            currentOption={scheduler.authType}
                                            onOptionChange={(authType) =>
                                                setScheduler((scheduler) => {
                                                    return { ...scheduler, authType };
                                                })
                                            }
                                            options={AZURE_AUTH_TYPE_OPTIONS}
                                            labelClassName="w-[135px]"
                                            menuClassName="w-[135px]"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <div className="min-w-[128px]">
                                        <Label text={'Account'} />
                                    </div>
                                    <div className="w-full">
                                        <TextInput
                                            onChange={(azureAccount) =>
                                                setScheduler((scheduler) => {
                                                    return { ...scheduler, azureAccount };
                                                })
                                            }
                                            value={scheduler.azureAccount}
                                            placeholder={'Enter account'}
                                            className="w-[100%]"
                                        />
                                    </div>
                                </div>
                                {scheduler.authType === AZURE_AUTH_TYPE_OPTIONS.sasToken.value ? (
                                    <div className="flex items-center">
                                        <div className="min-w-[128px]">
                                            <Label text={'Token'} />
                                        </div>
                                        <div className="w-full">
                                            <SFTextInput
                                                onChange={(azureSasToken) =>
                                                    setScheduler((scheduler) => {
                                                        return { ...scheduler, azureSasToken };
                                                    })
                                                }
                                                value={scheduler.azureSasToken}
                                                placeholder={'Enter token'}
                                                isSasToken={true}
                                                className="w-[100%]"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center">
                                        <div className="min-w-[128px]">
                                            <Label text={'Account key'} />
                                        </div>
                                        <div className="w-full">
                                            <TextInput
                                                onChange={(azureAccountKey) =>
                                                    setScheduler((scheduler) => {
                                                        return { ...scheduler, azureAccountKey };
                                                    })
                                                }
                                                value={scheduler.azureAccountKey}
                                                placeholder={'Enter account key'}
                                                type="password"
                                                className="w-[100%]"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center">
                                    <div className="min-w-[128px]">
                                        <Label text={'Container'} />
                                    </div>
                                    <div className="w-full">
                                        <TextInput
                                            onChange={(azureContainer) =>
                                                setScheduler((scheduler) => {
                                                    return { ...scheduler, azureContainer };
                                                })
                                            }
                                            value={scheduler.azureContainer}
                                            placeholder={'Enter container'}
                                            className="w-[100%]"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        {scheduler.fileSystem === FILE_SYSTEM_OPTIONS.smb.value && (
                            <SMBFields scheduler={scheduler} updateSchedulerField={updateSchedulerField} />
                        )}
                        {scheduler.fileSystem === FILE_SYSTEM_OPTIONS.aws.value && (
                            <>
                                <div className="flex items-center">
                                    <div className="min-w-[128px]">
                                        <Label text={'Access key ID'} />
                                    </div>
                                    <div className="w-full">
                                        <TextInput
                                            onChange={(awsAccessKeyId) =>
                                                setScheduler((scheduler) => {
                                                    return { ...scheduler, awsAccessKeyId };
                                                })
                                            }
                                            value={scheduler.awsAccessKeyId}
                                            placeholder={'Enter access key id'}
                                            className="w-[100%]"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <div className="min-w-[128px]">
                                        <Label text={'Secret access key'} />
                                    </div>
                                    <div className="w-full">
                                        <TextInput
                                            onChange={(awsSecretAccessKey) =>
                                                setScheduler((scheduler) => {
                                                    return { ...scheduler, awsSecretAccessKey };
                                                })
                                            }
                                            value={scheduler.awsSecretAccessKey}
                                            placeholder={'Enter secret access key'}
                                            type="password"
                                            className="w-[100%]"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <div className="min-w-[128px]">
                                        <Label text={'Region'} />
                                    </div>
                                    <div className="w-full">
                                        <TextInput
                                            onChange={(awsRegion) =>
                                                setScheduler((scheduler) => {
                                                    return { ...scheduler, awsRegion };
                                                })
                                            }
                                            value={scheduler.awsRegion}
                                            placeholder={'Enter region'}
                                            className="w-[100%]"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <div className="min-w-[128px]">
                                        <Label text={'Bucket name'} />
                                    </div>
                                    <div className="w-full">
                                        <TextInput
                                            onChange={(awsBucketName) =>
                                                setScheduler((scheduler) => {
                                                    return { ...scheduler, awsBucketName };
                                                })
                                            }
                                            value={scheduler.awsBucketName}
                                            placeholder={'Enter bucket name'}
                                            className="w-[100%]"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="flex items-center border-t-sf-gray-100 border-t-solid border-t-[1px] pt-4">
                            <div className="min-w-[128px] h-full pt-3">
                                <Label text="Add password" mandatory tooltipInfo={backupPasswordInfo} />
                            </div>
                            <div className="w-full">
                                <TextInput
                                    onChange={(password) => {
                                        setPasswordError();
                                        setScheduler((scheduler) => {
                                            return { ...scheduler, password };
                                        });
                                    }}
                                    value={scheduler.password}
                                    placeholder={'Add password'}
                                    type="password"
                                    className="w-[100%]"
                                />
                                {passwordError && <div className="text-10 text-red-600">{passwordError}</div>}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-hidden px-6">
                <div className="flex justify-end items-center">
                    <Button
                        onClick={cancelChanges}
                        text={'Cancel'}
                        buttonStyle={ButtonStyles.TertiarySolid}
                        className={'mr-2'}
                    />
                    <Button
                        buttonStyle={ButtonStyles.PrimarySolid}
                        disabled={!isScheduleValid}
                        onClick={handleSave}
                        text={'Save'}
                    />
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default SchedulerBackup;
